import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Paper,
  Tooltip,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fade,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeIcon,
  VolumeOff as VolumeMuteIcon,
  Close as CloseIcon,
  FastForward as ForwardIcon,
  FastRewind as RewindIcon,
  Speed as SpeedIcon,
  Refresh as RestartIcon
} from '@mui/icons-material';

/**
 * A reusable audio player component with playback controls.
 *
 * @param {Object} props
 * @param {string} props.audioSrc - URL of the audio file to play
 * @param {string} props.title - Title of the audio content
 * @param {Function} props.onClose - Function to call when closing the player
 * @param {boolean} props.autoPlay - Whether to autoplay the audio when src changes
 * @param {string} props.position - Position of the player ('top', 'bottom')
 */
const AudioPlayer = ({ audioSrc, title, onClose, autoPlay = false, position = 'bottom' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Refs
  const audioRef = useRef(new Audio());
  const intervalRef = useRef(null);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [isVisible, setIsVisible] = useState(true);

  // Set up audio when src changes
  useEffect(() => {
    const audio = audioRef.current;

    // Clean up previous audio
    audio.pause();
    setIsPlaying(false);
    clearInterval(intervalRef.current);

    if (!audioSrc) return;

    // Set up new audio
    audio.src = audioSrc;
    audio.volume = volume;
    audio.playbackRate = playbackSpeed;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setCurrentTime(0);
      if (autoPlay) {
        audio.play().catch(error => console.error("Autoplay failed:", error));
        setIsPlaying(true);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    // Set up event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    // Clean up function
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioSrc, autoPlay]);

  // Set up interval to update current time
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(audioRef.current.currentTime);
      }, 100);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying]);

  // Update volume when volume or mute state changes
  useEffect(() => {
    audioRef.current.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  // Update playback speed
  useEffect(() => {
    audioRef.current.playbackRate = playbackSpeed;
  }, [playbackSpeed]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      audio.pause();
      audio.src = '';
      clearInterval(intervalRef.current);
    };
  }, []);

  // Handle play/pause
  const togglePlayPause = () => {
    const audio = audioRef.current;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(error => console.error("Play failed:", error));
      setIsPlaying(true);
    }
  };

  // Enhanced close function that ensures audio is stopped first
  const handleClose = () => {
    // Stop audio playback
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);

    // Then call the onClose callback
    if (onClose) {
      onClose();
    }
  };

  // Handle time change (scrubbing)
  const handleTimeChange = (_, newValue) => {
    audioRef.current.currentTime = newValue;
    setCurrentTime(newValue);
  };

  // Handle volume change
  const handleVolumeChange = (_, newValue) => {
    setVolume(newValue);
    if (newValue > 0 && isMuted) {
      setIsMuted(false);
    } else if (newValue === 0 && !isMuted) {
      setIsMuted(true);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  // Handle rewind (5 seconds)
  const handleRewind = () => {
    const newTime = Math.max(0, audioRef.current.currentTime - 5);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Handle forward (5 seconds)
  const handleForward = () => {
    const newTime = Math.min(duration, audioRef.current.currentTime + 5);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Handle restart (back to beginning)
  const handleRestart = () => {
    audioRef.current.currentTime = 0;
    setCurrentTime(0);
    if (!isPlaying) {
      audioRef.current.play().catch(error => console.error("Play failed:", error));
      setIsPlaying(true);
    }
  };

  // Speed menu functions
  const handleSpeedMenuOpen = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };

  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    handleSpeedMenuClose();
  };

  // Format time (seconds -> MM:SS)
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Dynamic styles based on position
  const getPositionStyles = () => {
    const baseStyles = {
      position: 'fixed',
      left: 0,
      right: 0,
      zIndex: 1200, // Increased z-index to appear above UI elements
      transition: 'all 0.3s ease',
      padding: 2
    };

    if (position === 'top') {
      return {
        ...baseStyles,
        top: isVisible ? 0 : -100//,
        //borderRadius: '0 0 16px 16px',
      };
    } else {
      return {
        ...baseStyles,
        bottom: isVisible ? (isMobile ? 70 : 10) : -100//, // Increased bottom value to avoid overlapping
        //borderRadius: '16px 16px 0 0',
      };
    }
  };

  // Return null if no audio source
  if (!audioSrc) return null;

  // Playback speed options
  const speedOptions = [0.5, 0.75, 0.9, 1, 1.25, 1.5, 1.75, 2.0];

  return (
    <Fade in={isVisible}>
      <Paper
        elevation={4}
        sx={{
          ...getPositionStyles(),
          width: '100%',
          bgcolor: 'background.paper',
          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          pt: 0,
          pl: 0,
          pr: 0,
          pb: isMobile ? 0 : '80px',
          zIndex: 1200, // Higher z-index to prevent send button showing through
        }}
      >
        <Box sx={{
          px: { xs: 1, sm: 2 },
          py: 0,
          maxWidth: '600px',
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          //borderRadius: 2,
          boxShadow: 1
        }}>
          {/* Title and close button */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0
          }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                flexGrow: 0,
                fontSize: { xs: '0.4rem', sm: '1rem' }
              }}
            >

            </Typography>
            <IconButton size="small" onClick={handleClose} edge="end" aria-label="close player">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Progress bar and time */}
          <Box sx={{ width: '100%', mb: 0.5 }}>
            <Slider
              size="small"
              value={currentTime}
              max={duration || 100}
              onChange={handleTimeChange}
              aria-label="audio progress"
              sx={{
                color: 'primary.main',
                height: 4,
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                }
              }}
            />
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: -2,
              fontSize: '0.875rem'
            }}>
              <Typography variant="caption" color="text.secondary">
                {formatTime(currentTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatTime(duration)}
              </Typography>
            </Box>
          </Box>

          {/* Playback controls */}
          {/* Player controls redesigned for better spacing */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            mt: 1
          }}>
            {/* Main control section with evenly spaced buttons */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              maxWidth: '500px'
            }}>
              {/* Restart button */}
              <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Restart">
                  <IconButton
                    onClick={handleRestart}
                    aria-label="restart"
                    size="small"
                  >
                    <RestartIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Rewind button */}
              <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Rewind 5s">
                  <IconButton
                    onClick={handleRewind}
                    aria-label="rewind 5 seconds"
                    size="small"
                  >
                    <RewindIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Play/Pause button (larger) */}
              <Box sx={{ flex: '1.5 1 0', display: 'flex', justifyContent: 'center' }}>
                <Tooltip title={isPlaying ? "Pause" : "Play"}>
                  <IconButton
                    onClick={togglePlayPause}
                    color="primary"
                    aria-label={isPlaying ? "pause" : "play"}
                    size={isMobile ? "medium" : "large"}
                  >
                    {isPlaying ?
                      <PauseIcon fontSize={isMobile ? "medium" : "large"} /> :
                      <PlayIcon fontSize={isMobile ? "medium" : "large"} />
                    }
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Forward button */}
              <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Forward 5s">
                  <IconButton
                    onClick={handleForward}
                    aria-label="forward 5 seconds"
                    size="small"
                  >
                    <ForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Playback speed button */}
              <Box sx={{ flex: '1 1 0', display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="Playback speed">
                  <IconButton
                    onClick={handleSpeedMenuOpen}
                    aria-label="change playback speed"
                    aria-controls="speed-menu"
                    aria-haspopup="true"
                    size="small"
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      border: '1px solid',
                      borderColor: 'divider',
                      //borderRadius: '4px',
                      px: 0.5
                    }}>
                      {playbackSpeed}x
                    </Box>
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Volume control - hidden on mobile */}
              {!isMobile && (
                <Box sx={{ flex: '1.5 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Tooltip title={isMuted ? "Unmute" : "Mute"}>
                    <IconButton
                      onClick={toggleMute}
                      aria-label={isMuted ? "unmute" : "mute"}
                      size="small"
                    >
                      {isMuted ? <VolumeMuteIcon fontSize="small" /> : <VolumeIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Slider
                    size="small"
                    value={isMuted ? 0 : volume}
                    max={1}
                    step={0.01}
                    onChange={handleVolumeChange}
                    aria-label="volume"
                    sx={{
                      width: 60,
                      ml: 0.5,
                      color: 'secondary.main',
                      height: 4,
                      '& .MuiSlider-thumb': {
                        width: 10,
                        height: 10,
                      }
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Speed menu */}
            <Menu
              id="speed-menu"
              anchorEl={speedMenuAnchor}
              open={Boolean(speedMenuAnchor)}
              onClose={handleSpeedMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
            >
              {speedOptions.map((speed) => (
                <MenuItem
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  selected={playbackSpeed === speed}
                  dense
                >
                  {speed === 1 ? "Normal (1x)" : `${speed}x`}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

export default AudioPlayer;